import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  FormControl,
  Divider,
  Paper,
  IconButton,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { orderBy } from "lodash";
import store from "../../store";
import { bottomButton } from "../../common/styles";
//共通コンポーネント
import { Header, Pulldown, MainColorButton, Loading } from "../../components";
import { getReceiptHistory, getCustomerInfo } from "../../server/axios";
import { RECEIVE_MEDICATION_DETAILS } from "../../routes/path";
import { closeWindow } from "../../utils/getLineProfile";

/**
 * 受取薬履歴
 * @returns
 */
const MedicalHistory = () => {
  const navigate = useNavigate();
  // 選択された利用者
  const [user, setUser] = useState(0);
  // Loadingコンポネント表示非表示state
  const [loadingOpen, setLoadingOpen] = useState(false);
  // ご利用者名前選択肢
  const [userNames, setUserNames] = useState([]);
  // ご利用履歴データ
  const [data, setData] = useState([]);
  const [customer, setCustomer] = useState({});
  // 次のページ
  const [page, setPage] = useState(1);
  // 総ページ
  const [totalPage, setTotalPage] = useState(0);

  // 初回render反応しないように
  const firstRenderRef = useRef(0);

  const getData = async (params) => {
    setLoadingOpen(true);

    const res = await getReceiptHistory({
      customerId: params.customerId,
      siteId: params.siteId,
      familyId: user,
      page: params.page,
      mode: "list",
    });
    if (!res.data) {
      setData([]);
      setTotalPage(1);
      setPage(2);
      return;
    }
    const { totalCount, perPage, data: dataList } = res.data;
    if (params.page === 1) {
      setData(dataList);
    } else {
      let currentList = data;
      currentList = currentList.concat(dataList);
      setData(currentList);
    }

    const pageCnt = Math.ceil(totalCount / perPage);
    setTotalPage(pageCnt);
    if (pageCnt >= params.page) {
      setPage(params.page + 1);
    }
    setLoadingOpen(false);
  };

  let isLoading = false;

  const loadMore = async (scrollDistance) => {
    if (isLoading || page > totalPage) {
      return;
    }
    const leftDistance =
      document.getElementById("scrollContent").clientHeight - scrollDistance;
    const containerHeight =
      document.getElementById("scrollContainer").clientHeight + 10;
    if (leftDistance <= containerHeight) {
      isLoading = true;
      await getData({ ...customer, page: page });
      isLoading = false;
    }
  };

  // 初期化
  useEffect(() => {
    if (firstRenderRef.current === 0) {
      (async () => {
        setLoadingOpen(true);

        const selectLineInfo = (state) => state.prescription.lineInfo;
        const lineInfo = selectLineInfo(store.getState());

        const res = await getCustomerInfo({ lineId: lineInfo.lineId });
        const customerInfo = res.data;
        setCustomer(customerInfo);

        let userNamesTmp = [
          {
            id: 0,
            value: `${customerInfo.lastName}${customerInfo.firstName}`,
          },
        ];
        if (customerInfo.families && customerInfo.families.length > 0) {
          userNamesTmp = orderBy(
            userNamesTmp.concat(
              customerInfo.families.map((r) => {
                return {
                  id: r.familyId,
                  value: `${r.lastName}${r.firstName}`,
                };
              })
            ),
            ["id"]
          );
        }
        setUserNames(userNamesTmp);
        getData({ ...customerInfo, page: 1 });
      })();
    }
    firstRenderRef.current = firstRenderRef.current + 1;
  }, []);

  // ご利用者変更
  useEffect(() => {
    if (userNames.length > 0) {
      getData({ ...customer, page: 1 });
    }
  }, [user]);

  const handleClick = (item) => {
    navigate(RECEIVE_MEDICATION_DETAILS, {
      state: {
        customerId: customer.customerId,
        siteId: customer.siteId,
        familyId: user,
        userName: userNames[user].value,
        recordId: item.recordId,
      },
    });
  };

  return (
    <>
      <Loading open={loadingOpen} />
      <Header title="受取薬履歴" />
      <Stack
        height="75vh"
        marginTop="9vh"
        marginLeft="auto"
        marginRight="auto"
        spacing="6%"
      >
        {userNames.length > 0 && (
          <>
            <Stack
              direction="row"
              paddingLeft="3%"
              spacing="2%"
              alignItems="center"
            >
              <Box width="20%">
                <Typography>
                  <strong>ご利用者</strong>
                </Typography>
              </Box>
              <Box width="60%">
                <FormControl fullWidth>
                  <Pulldown
                    size="small"
                    withLabel={false}
                    withMenuProps={true}
                    items={userNames}
                    value={user}
                    setValue={(p) => setUser(p)}
                  />
                </FormControl>
              </Box>
            </Stack>

            <Paper
              elevation={0}
              variant="outlined"
              id="scrollContainer"
              sx={{ height: "69vh", overflowY: "scroll" }}
              onScroll={(e) => {
                loadMore(e.target.scrollTop);
              }}
            >
              <Stack id="scrollContent" divider={<Divider />}>
                {(data && data.length > 0) || (
                  <Stack direction="row" spacing="2%">
                    <Box
                      sx={{
                        width: "100%",
                        textAlign: "center",
                        paddingTop: "5vh",
                      }}
                    >
                      <Typography variant="h6">データがありません。</Typography>
                    </Box>
                  </Stack>
                )}
                {data &&
                  data.map((item, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing="2%"
                      onClick={() => handleClick(item)}
                    >
                      <Stack
                        key={index}
                        spacing="2%"
                        width="90%"
                        sx={{ paddingX: "3%", paddingY: "1%" }}
                      >
                        <Stack direction="row" spacing="2%" alignItems="center">
                          <Box width="50%">
                            <Typography>
                              <strong>処方日</strong>
                            </Typography>
                          </Box>
                          <Box width="50%">
                            <Typography>{item.supplyDate}</Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing="2%" alignItems="center">
                          <Box width="50%">
                            <Typography>
                              <strong>医療機関</strong>
                            </Typography>
                          </Box>
                          <Box width="50%">
                            <Typography>{item.publisherName}</Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing="2%" alignItems="center">
                          <Box width="50%">
                            <Typography>
                              <strong>処方薬局</strong>
                            </Typography>
                          </Box>
                          <Box width="50%">
                            <Typography>{item.dispensaryName}</Typography>
                          </Box>
                        </Stack>
                      </Stack>
                      <IconButton size="large">
                        <KeyboardArrowRightIcon fontSize="large" />
                      </IconButton>
                    </Stack>
                  ))}
              </Stack>
            </Paper>
          </>
        )}
      </Stack>
      <MainColorButton
        label="閉じる"
        onClick={() => closeWindow()}
        style={{ ...bottomButton, marginTop: "3vh" }}
      />
    </>
  );
};

export default MedicalHistory;
